import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { projects } from '@/api/client'
import { Plus, FolderKanban, ArrowRight } from 'lucide-react'
import { formatRelative } from '@/lib/utils'
import { DiscoveryProgressBar } from '@/components/DiscoveryProgressBar'

const projectStatusBadge: Record<string, string> = {
  Draft: 'badge-neutral',
  Discovering: 'badge-warning',
  Planning: 'badge-info',
  AwaitingApproval: 'badge-warning',
  Approved: 'badge-info',
  Executing: 'badge-warning',
  Auditing: 'badge-info',
  Cutover: 'badge-warning',
  Completed: 'badge-success',
  Cancelled: 'badge-neutral',
}

export function ProjectsPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projects.list(),
    refetchInterval: 5_000,  // a cada 5s para refletir progresso de Discovery
  })

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Projetos de migração</h1>
          <p className="text-sm text-slate-500 mt-1">Cada projeto representa uma migração entre tenants</p>
        </div>
        <Link to="/projects/new" className="btn-primary">
          <Plus className="h-4 w-4" /> Novo projeto
        </Link>
      </div>

      {isLoading ? (
        <div className="text-slate-500">Carregando...</div>
      ) : !data?.length ? (
        <div className="card p-12 text-center">
          <FolderKanban className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 mb-4">Nenhum projeto criado ainda</p>
          <Link to="/projects/new" className="btn-primary inline-flex">
            <Plus className="h-4 w-4" /> Criar primeiro projeto
          </Link>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {data.map((p) => (
            <Link
              key={p.id}
              to={
                // Sprint 4f Bug A: roteia direto pra tela certa baseado no status
                p.status === 'Executing' || p.status === 'Auditing' || p.status === 'Completed'
                  ? `/projects/${p.id}/execute`
                  : p.status === 'Approved'
                    ? `/projects/${p.id}/review`
                    : `/projects/${p.id}`
              }
              className="card p-5 hover:border-brand-300 hover:shadow-md transition-all group"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-slate-900 truncate group-hover:text-brand-700 transition-colors">
                    {p.name}
                  </h3>
                  {p.description && (
                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{p.description}</p>
                  )}
                </div>
                <span className={projectStatusBadge[p.status] || 'badge-neutral'}>{p.status}</span>
              </div>

              <div className="flex items-center gap-2 text-sm text-slate-600 mb-3">
                <span className="font-medium">{p.sourceTenantDisplayName || '?'}</span>
                <ArrowRight className="h-3 w-3 text-slate-400" />
                <span className="font-medium">{p.destinationTenantDisplayName || '?'}</span>
              </div>

              <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
                <span>Domínio: <strong className="text-slate-700">{p.domain}</strong></span>
                <span className="text-slate-300">•</span>
                <span>
                  {p.scopeType === 'WholeDomain'
                    ? 'Todo o domínio'
                    : `${p.scopeUpns?.length || 0} UPNs`}
                </span>
              </div>

              {/* Sprint 4f: só mostra discovery bar se projeto ainda está em fase de discovery/planning */}
              {p.latestDiscovery && (p.status === 'Draft' || p.status === 'Discovering' || p.status === 'Planning' || p.status === 'AwaitingApproval') && (
                <div className="mb-2">
                  <DiscoveryProgressBar job={p.latestDiscovery} compact />
                </div>
              )}

              <div className="text-xs text-slate-400 mt-3">
                Criado {formatRelative(p.createdAt)}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
